export type UserFlowMode = 'new' | 'returning'

export type UserFlowRouteName =
  | 'UserLogin'
  | 'UserOtp'
  | 'UserBasicDetails'
  | 'UserPanDetails'
  | 'UserOffers'
  | 'UserHome'

export type UserFlowSession = {
  active?: boolean
  customerId?: number
  fullName?: string
  phoneNumber?: string
  email?: string
  mode?: UserFlowMode
  lastRoute?: UserFlowRouteName
}

const USER_FLOW_SESSION_KEY = 'zet_user_flow_session'

const canUseSessionStorage = () =>
  typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined'

const getParsedSession = (): UserFlowSession => {
  if (!canUseSessionStorage()) {
    return {}
  }

  try {
    const value = window.sessionStorage.getItem(USER_FLOW_SESSION_KEY)
    return value ? JSON.parse(value) : {}
  } catch (error) {
    return {}
  }
}

const writeSession = (value: UserFlowSession) => {
  if (!canUseSessionStorage()) {
    return
  }

  window.sessionStorage.setItem(USER_FLOW_SESSION_KEY, JSON.stringify(value))
}

export const getUserFlowSession = (): UserFlowSession => getParsedSession()

export const setUserFlowSession = (value: Partial<UserFlowSession>) => {
  const nextValue = {
    ...getParsedSession(),
    ...value,
  }

  writeSession(nextValue)
  return nextValue
}

export const clearUserFlowSession = () => {
  if (!canUseSessionStorage()) {
    return
  }

  window.sessionStorage.removeItem(USER_FLOW_SESSION_KEY)
}

export const isUserFlowActive = () => Boolean(getParsedSession()?.active)

export const setUserFlowRoute = (route: UserFlowRouteName) =>
  setUserFlowSession({
    active: true,
    lastRoute: route,
  })

export const getUserFlowNextRoute = (): UserFlowRouteName => {
  const session = getParsedSession()

  if (
    session?.lastRoute &&
    session.lastRoute !== 'UserLogin' &&
    session.lastRoute !== 'UserOtp' &&
    session.lastRoute !== 'UserBasicDetails' &&
    session.lastRoute !== 'UserPanDetails' &&
    session.lastRoute !== 'UserOffers'
  ) {
    return session.lastRoute
  }

  return 'UserHome'
}

export const hasUserFlowLoginSession = () => {
  const session = getParsedSession()

  return Boolean(
    session?.active &&
      session?.customerId &&
      session?.lastRoute &&
      session?.lastRoute !== 'UserLogin',
  )
}
